/**
 * PWS Partner Water Testing — Customer-facing Landing Page (Feb 2026).
 *
 * Routes:
 *   • /pws-partner/:slug                                   ← public canonical URL
 *   • /preview/pws-partner-water-testing?p=PWS-XXX         ← preview alias (noindex)
 *
 * Slug resolution priority:
 *   1. `partner_slug` (attribution code like PWS-PHASE5-DEMO) — looked up
 *      via `/pws-partner-landing/by-partner-slug/{slug}`.
 *   2. Landing-page slug (like `demo-noosa`) — looked up via
 *      `/pws-partner-landing/by-slug/{slug}`.
 *   3. `/partners/resolve-slug/{slug}` — bare partner-record fallback so
 *      the hero + top-bar can still say "Partnering with <Business Name>"
 *      even without a published landing page.
 *
 * Partner data (name, region, phone, email, franchisee headshot, hero
 * intro override, franchisee_name) is pulled from the partner's own
 * landing-page row. Admins edit via `/admin/pws-partner-landing`;
 * partners can self-upload their headshot via the partner portal.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useParams, useLocation } from "react-router-dom";
import {
  MapPin, Phone, Mail, ArrowRight, ShieldCheck, FlaskConical, Home,
  Droplets, CheckCircle2, Package, Truck, TestTube, Send, ClipboardCheck,
  Users, HandHeart, Award, Wrench, HelpCircle, FileText, Info, Building2,
  Calendar,
} from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { useSEO } from "@/lib/useSEO";
import { buildPartnerLocalBusinessLd } from "@/lib/partnerLandingSeo";
import { ServiceAreasSection } from "@/components/partner/ServiceAreasSection";

// ── Test packages — plain-English blurbs pinned to the 3 AWARE
// products this page promotes. Prices displayed as "From $X" copy
// consistent with the approved mockup; live prices are locked in on
// the booking page which reads from the admin catalogue.
const PACKAGES = [
  {
    code: "basic",
    name: "AWARE 4-Point",
    subtitle: "Essential Water Safety Check",
    badge: "ESSENTIAL CHECK",
    badgeTone: "bg-emerald-100 text-emerald-800",
    priceFrom: 129,
    accent: "emerald",
    icon: Droplets,
    poster: "https://customer-assets-jt897jd0.emergentagent.net/job_pristine-lab/artifacts/2dcyd0qw_AWARE_4-Point_616x616.png",
    blurb: "Everyday drinking-water safety screen. Fast turnaround for tap, rainwater or town-water samples.",
    features: ["E. coli", "Coliforms", "pH", "Electrical Conductivity"],
  },
  {
    code: "tank",
    name: "AWARE Tank 360",
    subtitle: "Comprehensive Tank Water Test",
    badge: "MOST POPULAR",
    badgeTone: "bg-cyan-100 text-cyan-800",
    priceFrom: 209,
    accent: "cyan",
    icon: Home,
    poster: "https://customer-assets.emergentagent.com/job_pristine-lab/artifacts/r1d9rxvg_AWARE_Tank_360_Square.png",
    blurb: "Full rainwater-tank test with plain-English recommendations for tank cleaning, first-flush and filtration.",
    features: ["Microbiological Testing", "Chemical Analysis", "Nutrients", "Metals", "And more"],
  },
  {
    code: "bore",
    name: "AWARE Bore 360",
    subtitle: "Comprehensive Bore Water Test",
    badge: "BEST FOR BORE WATER",
    badgeTone: "bg-purple-100 text-purple-800",
    priceFrom: 229,
    accent: "purple",
    icon: FlaskConical,
    poster: "https://customer-assets.emergentagent.com/job_pristine-lab/artifacts/dd3la74m_AWARE_Bore_360_Square.png",
    blurb: "Comprehensive bore-water analysis covering hardness, iron, salts and bacteria — with treatment guidance.",
    features: ["Microbiological Testing", "Chemical Analysis", "Nutrients", "Metals", "And more"],
  },
];

const REASONS = [
  { icon: MapPin,         title: "Local Knowledge That Matters",   body: "Your local Partner understands water tanks, bores, pumps, filtration and pipework — and the practical conditions affecting household water systems in your region." },
  { icon: HelpCircle,     title: "Help Choosing the Right Test",   body: "Not every property needs the same test. Your Partner can help you pick the most appropriate AWARE package for your water source." },
  { icon: FlaskConical,   title: "Professional Lab You Can Trust", body: "AWARE Water Testing provides reliable, independent laboratory analysis and clear, easy-to-read reports." },
  { icon: Wrench,         title: "Practical Solutions When You Need Them", body: "If something needs attention, your Partner may be able to help with tank cleaning, filtration, UV, pumps or system improvements." },
  { icon: Users,          title: "One Local Team, End-to-End",     body: "Rather than being left with a report and no next step, you have a local specialist available to help you understand the result and take practical action." },
  { icon: ShieldCheck,    title: "Backed by the PWS Network",      body: "Testing performed by AWARE. Local support delivered by your Pristine Water Systems Partner — the same team you can call again." },
];

const STEPS = [
  { icon: ClipboardCheck, title: "Order Your Test",  body: "Choose the test that suits your tank, bore or household water and place your order online in minutes." },
  { icon: Package,        title: "Kit Posted to You", body: "A complete testing kit — with sample bottles, labels and instructions — is posted to your nominated address." },
  { icon: TestTube,       title: "Collect Your Sample", body: "Follow the included instructions to collect your water sample correctly." },
  { icon: Send,           title: "Return Your Sample", body: "Drop the pre-paid express-post return tube in any Australia Post box — your sample is on its way to the AWARE laboratory." },
  { icon: FileText,       title: "Results & Support", body: "Receive your Certificate of Analysis, plain-English summary, and practical recommendations — with local Partner support if needed." },
];

const WHAT_YOU_RECEIVE = [
  { icon: FileText,      title: "Professional Certificate of Analysis", body: "Detailed laboratory results you can trust." },
  { icon: Info,          title: "Easy-to-Understand Summary",           body: "Plain English summary of your water quality." },
  { icon: HelpCircle,    title: "Items Requiring Attention",            body: "Clearly identified issues that may need action." },
  { icon: Wrench,        title: "Practical Recommendations",            body: "Actionable advice tailored to your water." },
  { icon: FileText,      title: "Water Health Record",                  body: "Your results securely stored for future reference." },
  { icon: HandHeart,     title: "Local Support When Needed",            body: "Your local PWS Partner is here to help." },
];

const FAQS = [
  { q: "How does the water test kit arrive?",
    a: "Once you order online, your local PWS Partner triggers your kit to be posted directly to the address you provided. Most kits arrive within 1–3 business days via Australia Post. The kit includes sample bottles, labels, a return satchel and step-by-step instructions." },
  { q: "How long does it take to get my results back?",
    a: "Turnaround time is typically 1–2 business days from when AWARE Water Testing receives your sample. You'll receive an email as soon as your Certificate of Analysis and plain-English summary are ready." },
  { q: "What type of water can I test — tank, bore, drinking or pool?",
    a: "You can test rainwater tanks, bore water, or spring/dam-fed household water. Each AWARE test package is designed for a specific water source, so pick the one that matches yours — or ask your local PWS Partner to help you choose." },
  { q: "How are the parameters tested?",
    a: "Every sample is processed at AWARE Water Testing's laboratory using established analytical methods for each parameter — culture-based microbiological testing for E. coli and coliforms, spectrophotometric and titrimetric chemistry for nutrients and metals, and calibrated instrument readings for pH, conductivity and turbidity. Each result on your Certificate of Analysis references the method used." },
  { q: "What happens if my water fails the test?",
    a: "Your report will clearly flag any parameters outside acceptable ranges, in plain English. Your local PWS Partner then reaches out to talk you through the result and, if you'd like, recommend practical solutions — tank cleaning, filtration, UV disinfection, or a full system review." },
  { q: "Do I need to be home when the kit is delivered?",
    a: "No. The kit is posted as a standard parcel — leave a safe-drop instruction with Australia Post if you're not usually home. Collecting the sample takes about 10 minutes on a day that suits you." },
  { q: "How much does a water test cost?",
    a: "AWARE test packages start from $129 including GST. Full pricing is shown on the booking page — the fee covers the posted kit, laboratory analysis and your Certificate of Analysis. There are no hidden charges." },
  { q: "Who do I contact if I have questions about my results?",
    a: "Your local Pristine Water Systems Partner. They're the same team that helped set up your test, they know your area, and they can talk you through what your result means in practical terms — no upsell pressure." },
];

function bookingHref(slug, testCode) {
  const params = new URLSearchParams();
  if (slug) params.set("p", slug);
  if (testCode) params.set("test", testCode);
  const q = params.toString();
  return q ? `/book-water-test?${q}` : "/book-water-test";
}

function PriceTag({ from, accent }) {
  const toneMap = { emerald: "text-emerald-700", cyan: "text-cyan-700", purple: "text-purple-700" };
  return (
    <div className="text-right">
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">From</div>
      <div className={`text-3xl font-bold tabular-nums ${toneMap[accent] || "text-slate-900"}`}>
        ${from}<span className="text-xs font-normal text-slate-500 ml-1">inc. GST</span>
      </div>
    </div>
  );
}

function PackageCard({ pkg, slug }) {
  return (
    <div
      data-testid={`preview-pkg-${pkg.code}`}
      className="group flex flex-col rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm hover:shadow-lg hover:border-slate-300 transition-all"
    >
      {/* Full-width square poster — the branded AWARE 4-Point / Tank
          360 / Bore 360 poster does the visual heavy lifting; the
          card frame stays minimal below. Square aspect matches the
          `*_Square.png` source assets. `object-top` keeps the AWARE
          logo aligned across all three cards (the 4-Point poster
          has slightly more top whitespace than Tank/Bore). */}
      <div className="relative aspect-square bg-slate-100 overflow-hidden">
        <img
          src={pkg.poster}
          alt={`${pkg.name} poster`}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
          loading="lazy"
        />
      </div>
      <div className="p-5 sm:p-6 flex flex-col flex-1">
        <h3 className="text-lg font-bold text-slate-900">{pkg.name}</h3>
        {/* Fixed 3-line description height so the "Book this test"
            button aligns across all three cards regardless of copy
            length. */}
        <p className="text-sm text-slate-600 mt-2 leading-relaxed min-h-[4.5rem]">
          {pkg.blurb}
        </p>
        <Link
          to={bookingHref(slug, pkg.code)}
          data-testid={`preview-pkg-cta-${pkg.code}`}
          className="mt-5 inline-flex items-center justify-center gap-2 rounded-full border-2 border-slate-200 hover:border-cyan-600 hover:text-cyan-700 text-slate-800 text-sm font-semibold px-5 py-2.5 transition-colors"
        >
          Book this test <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

export default function PwsPartnerWaterTestingPreview() {
  const [params] = useSearchParams();
  const { slug: slugFromPath } = useParams();
  const { pathname } = useLocation();
  // Public canonical route (`/pws-partner/:slug`) is fully indexable.
  // The preview alias route stays noindex until product signs off.
  const isPreviewAlias = pathname.startsWith("/preview/");
  // Slug can arrive via ?p= / ?slug= (attribution links) or as a path
  // parameter (`/pws-partner/:slug` — landing-page slug).
  const slugFromInput = (
    slugFromPath || params.get("p") || params.get("slug") || ""
  ).trim();
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(false);

  // Feb 2026 SEO — per-partner LocalBusiness JSON-LD. Emitted only on
  // the canonical `/pws-partner/:slug` route (never on the noindex
  // preview alias). Parents to the site-wide Organization node via
  // `@id` so Google collapses the entity graph cleanly. Uses the
  // partner's `service_suburbs` for `areaServed` — the whole point of
  // this schema is to surface each partner on suburb-level local
  // searches ("water testing noosa", "bore test buderim" etc.).
  const canonicalSlug = partner?.slug || partner?.partner_slug || slugFromInput;
  const partnerCanonicalUrl = canonicalSlug
    ? `https://awarewatertesting.com.au/pws-partner/${encodeURIComponent(canonicalSlug)}`
    : undefined;
  const partnerJsonLd = (!isPreviewAlias && partner?.business_name && partnerCanonicalUrl)
    ? buildPartnerLocalBusinessLd({
        businessName:       partner.business_name,
        canonicalUrl:       partnerCanonicalUrl,
        regionLabel:        partner.region_label,
        serviceSuburbs:     partner.service_suburbs,
        phoneDisplay:       partner.phone_display,
        emailDisplay:       partner.email_display,
        franchiseePhotoUrl: partner.franchisee_photo_url,
      })
    : null;

  useSEO(
    isPreviewAlias
      ? {
          title: "PREVIEW — PWS Partner Water Testing",
          description: "Preview-only landing page. Not for public indexing.",
          noindex: true,
          includeBaseline: false,
        }
      : {
          title: partner?.business_name
            ? `${partner.business_name} — Water Testing | Pristine Water Systems`
            : "Water Testing for Tank & Bore Homeowners | Pristine Water Systems",
          description: partner?.business_name
            ? `Book a professional water test with ${partner.business_name}. AWARE lab analysis with local PWS Partner support.`
            : "Book a professional water test through your local Pristine Water Systems Partner. Kits posted, AWARE lab analysis, plain-English results.",
          // Canonical URL — only present on the real /pws-partner/:slug
          // route (never on the preview alias) so Google indexes each
          // partner page under its own stable URL.
          url:    partnerCanonicalUrl,
          jsonLd: partnerJsonLd,
        }
  );

  // Only stamp the stricter "noarchive, nosnippet" directives on the
  // preview alias — the canonical `/pws-partner/:slug` route needs to
  // be crawlable so Google can index individual partner pages.
  useEffect(() => {
    if (!isPreviewAlias) return;
    const el = document.querySelector('meta[name="robots"]');
    if (el) el.setAttribute("content", "noindex, nofollow, noarchive, nosnippet");
  }, [isPreviewAlias]);

  useEffect(() => {
    if (!slugFromInput) { setPartner(null); return; }
    let cancelled = false;
    setLoading(true);
    // Resolution order:
    //   1) partner-slug attribution code (PWS-XXX) → richest snapshot
    //   2) landing-page slug (e.g. demo-noosa)     → same shape
    //   3) partner-record resolve-slug fallback    → hero name + region only
    // Each step 404s cleanly to the next.
    const tryPartnerSlug = () =>
      api.get(`/pws-partner-landing/by-partner-slug/${encodeURIComponent(slugFromInput)}`);
    const tryLandingSlug = () =>
      api.get(`/pws-partner-landing/by-slug/${encodeURIComponent(slugFromInput)}`);
    const tryResolveSlug = () =>
      api.get(`/partners/resolve-slug/${encodeURIComponent(slugFromInput)}`);

    tryPartnerSlug()
      .then(({ data }) => { if (!cancelled) setPartner(data); })
      .catch(() => tryLandingSlug()
        .then(({ data }) => { if (!cancelled) setPartner(data); })
        .catch(() => tryResolveSlug()
          .then(({ data }) => {
            if (cancelled) return;
            // Normalise the resolve-slug shape into the landing-page
            // shape the JSX below expects.
            setPartner({
              partner_slug:   data?.partner_slug,
              business_name:  data?.business_name,
              region_label:   data?.franchise_region || "",
              phone_display:  "",
              email_display:  "",
              franchisee_logo_url:  "",
              franchisee_photo_url: "",
              hero_intro_override:  "",
            });
          })
          .catch(() => { if (!cancelled) setPartner(null); })))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slugFromInput]);

  // Feb 2026 — Rolling trust counter. Fetches unique_views over the
  // last 30 days for the same `?p=` slug. Fails silently and simply
  // hides the counter when the value is 0 / unknown.
  const [trust, setTrust] = useState(null);
  useEffect(() => {
    if (!slugFromInput) { setTrust(null); return; }
    let cancelled = false;
    api.get(`/pws-partner-landing/trust-stats/${encodeURIComponent(slugFromInput)}`)
      .then(({ data }) => { if (!cancelled) setTrust(data); })
      .catch(() => { if (!cancelled) setTrust(null); });
    return () => { cancelled = true; };
  }, [slugFromInput]);

  // Fallbacks so QA can review layout without a real partner slug.
  const partnerName          = partner?.business_name || "Your Local Pristine Water Systems Partner";
  const partnerRegion        = partner?.region_label || partner?.franchise_region || "your area";
  const partnerPhone         = partner?.phone_display || partner?.phone   || partner?.contact_phone;
  const partnerEmail         = partner?.email_display || partner?.email   || partner?.contact_email;
  const partnerFranchiseeName = partner?.franchisee_name || "";
  const partnerLogo   = partner?.franchisee_logo_url || partner?.logo_url;
  const partnerHero   = partner?.franchisee_photo_url || partner?.hero_image_url;
  const partnerBio    = partner?.hero_intro_override || partner?.short_bio || partner?.about_snippet;

  // Booking CTAs always attribute using the ORIGINAL `?p=` slug the
  // customer arrived on — preserves the existing attribution contract
  // whether or not a landing page exists.
  const slug = partner?.partner_slug || slugFromInput || null;

  // Hero image is FIXED per the approved mockup — the customer scene
  // (technician + homeowner at rainwater tank). Partner-specific staff
  // photos live in the "Your Local Water Specialists" overlay card
  // (see `partnerHero` usage lower down), not the hero itself.
  const heroImage = "https://customer-assets-jt897jd0.emergentagent.net/job_pristine-lab/artifacts/ivq41h6v_ChatGPT%20Image%20Jul%2026%2C%202026%2C%2009_39_28%20AM.png";

  // Default franchisee headshot — used in the "Your Local Water
  // Specialists" card when the partner has no `franchisee_photo_url`
  // set yet. Admins can override per-partner via the landing page
  // edit form.
  const FRANCHISEE_HEADSHOT_URL = "https://customer-assets-jt897jd0.emergentagent.net/job_pristine-lab/artifacts/gtznytug_image.png";

  // Official brand logos — Feb 2026. PWS + AWARE transparent PNGs.
  // Not personalisable by partner — every PWS franchisee ships under
  // these two lockups.
  const PWS_LOGO_URL   = "https://customer-assets.emergentagent.com/job_pristine-lab/artifacts/umqjuyjn_pristine_water_systems_clear.png";
  const AWARE_LOGO_URL = "https://customer-assets.emergentagent.com/job_pristine-lab/artifacts/zi3hrojv_AWARE_LOGO_header_large-removebg-preview.png";

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="preview-pws-partner-page">
      {/* Preview banner — only shown on the /preview/... alias route.
          The canonical /pws-partner/:slug URL is the real public page. */}
      {isPreviewAlias && (
        <div
          data-testid="preview-banner"
          className="bg-amber-500 text-slate-900 text-xs font-bold text-center py-1.5 px-3"
        >
          PREVIEW ONLY · noindex, nofollow · Not linked from public navigation
        </div>
      )}

      {/* Top contact bar — deep navy, full width. Generic Australian
          copy shown when no partner slug resolves; personalised region
          replaces the copy once a partner is attributed. */}
      <div className="bg-[#0b1f4d] text-white text-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-3">
          <span className="flex items-center gap-2">
            <MapPin className="w-4 h-4 flex-shrink-0" />
            {partner
              ? <>Proudly supporting homeowners in <strong>{partnerRegion}</strong></>
              : <>Proudly supporting Australian homes, farms and businesses.</>}
          </span>
          <div className="flex flex-wrap items-center gap-6">
            {partnerPhone && (
              <a href={`tel:${partnerPhone}`} data-testid="preview-top-phone" className="flex items-center gap-2 hover:text-cyan-300 transition-colors">
                <Phone className="w-4 h-4" /> {partnerPhone}
              </a>
            )}
            {partnerEmail && (
              <a href={`mailto:${partnerEmail}`} data-testid="preview-top-email" className="flex items-center gap-2 hover:text-cyan-300 transition-colors">
                <Mail className="w-4 h-4" /> {partnerEmail}
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Header — dual logo lockup (PWS | AWARE) with divider, primary
          nav, and filled teal Book CTA. Matches the approved mockup. */}
      <header className="border-b border-slate-100 bg-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between gap-6">
          {/* Dual logo block — official brand lockups */}
          <div className="flex items-center gap-5">
            <img
              data-testid="preview-header-pws-lockup"
              src={PWS_LOGO_URL}
              alt="Pristine Water Systems"
              className="h-12 sm:h-14 w-auto object-contain"
            />
            <div className="w-px h-12 bg-slate-200" aria-hidden="true" />
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="text-[10px] text-slate-500 mb-1">Your local Partner of</span>
              <img
                data-testid="preview-header-aware-lockup"
                src={AWARE_LOGO_URL}
                alt="AWARE Water Testing"
                className="h-8 w-auto object-contain"
              />
            </div>
          </div>
          <nav className="hidden lg:flex items-center gap-7 text-sm font-medium text-slate-700">
            <a href="#why-test" className="hover:text-cyan-700 transition-colors">Why Test</a>
            <a href="#our-tests" className="hover:text-cyan-700 transition-colors">Our Tests</a>
            <a href="#how-it-works" className="hover:text-cyan-700 transition-colors">How It Works</a>
            <a href="#local-partner" className="hover:text-cyan-700 transition-colors">About Your Local PWS</a>
            <a href="#faqs" className="hover:text-cyan-700 transition-colors">FAQs</a>
          </nav>
          <Link
            to={bookingHref(slug)}
            data-testid="preview-header-book-cta"
            className="inline-flex items-center gap-1.5 rounded-md bg-cyan-700 hover:bg-cyan-800 text-white text-sm font-semibold px-5 py-3 transition-colors whitespace-nowrap shadow-sm"
          >
            Book Your Water Test
          </Link>
        </div>
      </header>

      {/* Hero — full-bleed image on the right that visually extends
          under the text column; navy "Your Local Water Specialists"
          card floats over the bottom-right of the image. */}
      <section className="relative bg-gradient-to-br from-cyan-50/60 via-white to-white overflow-hidden">
        {/* Hero right column — image + floating specialists card share
            a single positioned container so the card follows the
            bottom of the image (not the bottom of the section).
            Shrinking the image height now naturally moves the card
            up with it. */}
        <div
          data-testid="preview-hero-image-wrap"
          className="hidden lg:block absolute top-10 right-4 w-[48%] pointer-events-none"
        >
          <div className="relative aspect-[3/2]">
            <img
              src={heroImage}
              alt="Pristine Water Systems technician assisting a homeowner at their water tank"
              className="w-full h-full object-cover object-top rounded-3xl shadow-xl"
              loading="eager"
              data-testid="preview-hero-image"
            />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-8 lg:pt-10 lg:pb-16 relative">
          <div className="lg:w-[50%] lg:pr-4 relative z-10">
            <h1
              data-testid="preview-hero-headline"
              className="font-bold leading-[1.02] tracking-tight text-[#0b1f4d]"
            >
              <span className="block text-2xl sm:text-3xl lg:text-4xl font-semibold text-slate-700">
                Tank Water, Bore Water<span className="text-cyan-700">…</span>
              </span>
              <span className="block mt-2 text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight">
                It&rsquo;s Your Water
              </span>
              <span className="block mt-3 text-2xl sm:text-3xl lg:text-4xl uppercase font-bold text-cyan-700 leading-[1.1]">
                Know what&rsquo;s in it, before you drink it!
              </span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
              Your local Pristine Water Systems team makes water testing simple. We post your test kit directly to you. Your sample is professionally analysed by <strong className="text-slate-900">AWARE Water Testing</strong>. You get clear results and <strong>practical local support</strong> every step of the way.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to={bookingHref(slug)}
                data-testid="preview-hero-book-cta"
                className="inline-flex items-center gap-2 rounded-md bg-cyan-700 hover:bg-cyan-800 text-white text-base font-semibold px-6 py-3.5 transition-colors shadow-sm"
              >
                <Calendar className="w-5 h-5" /> Book Your Water Test
              </Link>
              <a
                href="#our-tests"
                data-testid="preview-hero-view-tests-cta"
                className="inline-flex items-center gap-2 rounded-md border-2 border-cyan-700 bg-white text-cyan-700 hover:bg-cyan-50 text-base font-semibold px-6 py-3 transition-colors"
              >
                <FlaskConical className="w-5 h-5" /> View Testing Options
              </a>
            </div>
            {/* Trust points */}
            <div className="mt-10 grid sm:grid-cols-3 gap-5 text-sm max-w-2xl">
              <div className="flex items-start gap-2.5">
                <FlaskConical className="w-6 h-6 text-cyan-600 flex-shrink-0 mt-0.5" />
                <span className="leading-snug text-slate-700"><strong className="block text-slate-900">Professional</strong>laboratory testing</span>
              </div>
              <div className="flex items-start gap-2.5">
                <FileText className="w-6 h-6 text-cyan-600 flex-shrink-0 mt-0.5" />
                <span className="leading-snug text-slate-700"><strong className="block text-slate-900">Easy-to-understand</strong>reporting</span>
              </div>
              <div className="flex items-start gap-2.5">
                <HandHeart className="w-6 h-6 text-cyan-600 flex-shrink-0 mt-0.5" />
                <span className="leading-snug text-slate-700"><strong className="block text-slate-900">Local support</strong>when you need it</span>
              </div>
            </div>
            {/* Rolling trust counter — hidden when 0 */}
            {trust && trust.unique_views > 0 && (
              <div
                data-testid="preview-hero-trust-counter"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-cyan-50 border border-cyan-200 text-cyan-900 text-xs font-medium px-3 py-1.5"
              >
                <Users className="w-3.5 h-3.5" />
                Seen by <strong className="tabular-nums">{trust.unique_views.toLocaleString()}</strong> homeowners in the last {trust.window_days} days
              </div>
            )}
          </div>

          {/* Mobile-only hero image (stacked). On lg+ this is hidden
              because the full-bleed absolute image above takes over. */}
          <div className="mt-8 lg:hidden">
            <img
              src={heroImage}
              alt="Pristine Water Systems technician assisting a homeowner at their water tank"
              className="w-full rounded-2xl object-cover shadow-xl aspect-[4/3]"
              loading="eager"
              data-testid="preview-hero-image-mobile"
            />
          </div>

          {/* Floating "Your Local Water Specialists" card — landscape
              layout matching the approved mockup. 3 columns: round
              photo | franchisee name + description | vertical
              benefits list. Positioned to overhang the bottom-right
              of the image and extend into the cream area beyond the
              image's right edge for a strong overlap effect. */}
          <div
            className="mt-8 lg:mt-0 lg:absolute lg:bottom-4 lg:right-0 lg:w-[50%] lg:z-20 max-w-3xl"
          >
            <div
              data-testid="preview-hero-specialists-card"
              className="rounded-2xl bg-[#0b1f4d] text-white p-5 sm:p-6 shadow-2xl border border-cyan-400/30"
            >
              <div className="grid sm:grid-cols-[auto_1fr_auto] gap-x-5 gap-y-3 items-center">
                <div
                  data-testid="preview-hero-specialists-photo"
                  className="w-20 h-20 rounded-full bg-white/10 border-2 border-white/30 overflow-hidden flex-shrink-0"
                >
                  <img
                    src={partnerHero || FRANCHISEE_HEADSHOT_URL}
                    alt={partnerFranchiseeName ? `${partnerFranchiseeName} — ${partnerName}` : `${partnerName} franchisee`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="min-w-0">
                  {partnerFranchiseeName ? (
                    <>
                      <h3
                        data-testid="preview-hero-franchisee-name"
                        className="text-base sm:text-lg font-bold text-white leading-tight"
                      >
                        {partnerFranchiseeName}
                      </h3>
                      <div
                        data-testid="preview-hero-business-name"
                        className="text-xs font-semibold text-cyan-300 mt-0.5"
                      >
                        {partnerName}
                      </div>
                    </>
                  ) : (
                    <h3 className="text-base sm:text-lg font-bold text-white leading-tight">
                      Your Local Water Specialists
                    </h3>
                  )}
                  <p className="text-xs sm:text-sm text-cyan-100/90 mt-1.5 leading-relaxed">
                    We live and work in your community and understand local water conditions. We&rsquo;re here to help you get clear answers and practical solutions.
                  </p>
                  {(partnerPhone || partnerEmail) && (
                    <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] sm:text-xs">
                      {partnerPhone && (
                        <a
                          href={`tel:${partnerPhone}`}
                          data-testid="preview-hero-specialists-phone"
                          className="inline-flex items-center gap-1 text-white hover:text-cyan-300 transition-colors"
                        >
                          <Phone className="w-3 h-3" /> {partnerPhone}
                        </a>
                      )}
                      {partnerEmail && (
                        <a
                          href={`mailto:${partnerEmail}`}
                          data-testid="preview-hero-specialists-email"
                          className="inline-flex items-center gap-1 text-white hover:text-cyan-300 transition-colors break-all"
                        >
                          <Mail className="w-3 h-3" /> {partnerEmail}
                        </a>
                      )}
                    </div>
                  )}
                </div>
                <ul className="grid grid-cols-1 gap-1.5 text-xs sm:text-sm border-l border-white/10 sm:pl-5">
                  {["Local expertise", "Honest advice", "Practical solutions", "Ongoing support"].map((label) => (
                    <li key={label} className="flex items-center gap-2">
                      <span className="w-4 h-4 rounded-full bg-cyan-500/20 border border-cyan-400 flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 className="w-2.5 h-2.5 text-cyan-300" />
                      </span>
                      <span className="text-white/95 whitespace-nowrap">{label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Test Section */}
      <section id="why-test" className="bg-slate-50 py-14">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-slate-900">
            Why Homeowners on Tank or Bore Water Choose Their Local PWS Partner
          </h2>
          <div className="w-16 h-1 bg-cyan-600 mx-auto mt-3" />
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {REASONS.map((r) => {
              const Icon = r.icon;
              return (
                <div
                  key={r.title}
                  data-testid={`preview-reason-${r.title.replace(/\s+/g, "-").toLowerCase()}`}
                  className="rounded-2xl border border-slate-200 bg-white p-5 hover:shadow-md transition-shadow"
                >
                  <div className="w-11 h-11 rounded-xl bg-cyan-100 flex items-center justify-center text-cyan-700 mb-3">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="text-base font-bold text-slate-900">{r.title}</h3>
                  <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{r.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Test Packages */}
      <section id="our-tests" className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center">
            <div className="inline-flex items-center gap-2">
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">Choose the Right Test for Your Water</h2>
              <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full bg-cyan-100 text-cyan-800">
                Popular Tests
              </span>
            </div>
            <p className="text-slate-600 text-sm mt-2 max-w-2xl mx-auto">
              Live pricing and inclusions are locked in on the booking page, which reads directly from the AWARE product catalogue.
            </p>
          </div>
          <div className="mt-8 grid md:grid-cols-3 gap-5">
            {PACKAGES.map((pkg) => (
              <PackageCard key={pkg.code} pkg={pkg} slug={slug} />
            ))}
          </div>
          <div className="mt-6 rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 flex flex-wrap items-center justify-center gap-3">
            <HelpCircle className="w-4 h-4 text-slate-500" />
            <span className="text-sm text-slate-600">Not sure which test you need? We can help.</span>
            <Link
              to={bookingHref(slug)}
              data-testid="preview-help-me-choose"
              className="text-sm font-semibold text-cyan-700 hover:text-cyan-800 underline underline-offset-2"
            >
              Help Me Choose
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="bg-gradient-to-b from-cyan-50/50 to-white py-14">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">How It Works</h2>
            <p className="text-slate-600 text-sm mt-2">Simple steps from ordering to clear results and support.</p>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-5 gap-6 relative">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.title}
                  data-testid={`preview-step-${i + 1}`}
                  className="text-center relative"
                >
                  <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-cyan-700 text-white text-sm font-bold shadow">
                    {i + 1}
                  </div>
                  <div className="mt-3 mx-auto w-16 h-16 rounded-2xl bg-white border border-cyan-100 flex items-center justify-center text-cyan-700 shadow-sm">
                    <Icon className="w-7 h-7" />
                  </div>
                  <h3 className="mt-3 text-sm font-bold text-slate-900">{s.title}</h3>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed px-1">{s.body}</p>
                </div>
              );
            })}
          </div>
          <div className="mt-10 rounded-2xl bg-cyan-50 border border-cyan-200 p-5 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-8 h-8 text-cyan-700" />
              <div>
                <h3 className="text-base font-bold text-slate-900">Your Water. Our Priority.</h3>
                <p className="text-sm text-slate-600 mt-0.5 max-w-md">
                  We take the guesswork out of water quality so you can make informed decisions with confidence.
                </p>
              </div>
            </div>
            <Link
              to={bookingHref(slug)}
              data-testid="preview-mid-book-cta"
              className="inline-flex items-center gap-1.5 rounded-lg bg-cyan-700 hover:bg-cyan-800 text-white text-sm font-semibold px-5 py-2.5 transition-colors"
            >
              Book Your Water Test <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* What You Receive */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">More Than Just a Laboratory Result</h2>
            <p className="text-slate-600 text-sm mt-2 max-w-2xl mx-auto">
              You will not simply receive a page of unexplained numbers. Your report is designed to help you understand what was found and what practical steps may be considered next.
            </p>
          </div>
          <div className="mt-8 grid sm:grid-cols-2 md:grid-cols-3 gap-5">
            {WHAT_YOU_RECEIVE.map((w) => {
              const Icon = w.icon;
              return (
                <div
                  key={w.title}
                  data-testid={`preview-receive-${w.title.replace(/\s+/g, "-").toLowerCase()}`}
                  className="rounded-xl border border-slate-100 bg-slate-50/40 p-4 flex items-start gap-3"
                >
                  <div className="w-9 h-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-cyan-700 flex-shrink-0">
                    <Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-slate-900">{w.title}</h3>
                    <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">{w.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQs — 8 practical Q&As. Uses the shadcn Accordion so only
          one panel is open at a time on mobile (nudges reviewers to
          scan rather than scroll a wall of text). */}
      <section id="faqs" className="bg-white py-14 border-t border-slate-100">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0b1f4d]">Frequently Asked Questions</h2>
            <p className="text-slate-600 text-sm mt-2 max-w-2xl mx-auto">
              Quick answers to the questions we hear most from homeowners on tank or bore water.
              Still unsure? Your local PWS Partner is one call away.
            </p>
          </div>
          <Accordion
            type="single"
            collapsible
            className="w-full space-y-2"
            data-testid="preview-faqs-accordion"
          >
            {FAQS.map((faq, idx) => (
              <AccordionItem
                key={faq.q}
                value={`faq-${idx}`}
                data-testid={`preview-faq-item-${idx}`}
                className="rounded-lg border border-slate-200 bg-slate-50/50 px-4 data-[state=open]:bg-white data-[state=open]:shadow-sm"
              >
                <AccordionTrigger className="text-left text-sm sm:text-base font-semibold text-[#0b1f4d] hover:no-underline py-4">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-slate-700 leading-relaxed pb-4">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="text-center mt-8">
            <Link
              to={bookingHref(slug)}
              data-testid="preview-faqs-book-cta"
              className="inline-flex items-center gap-2 rounded-md bg-cyan-700 hover:bg-cyan-800 text-white text-sm font-semibold px-5 py-3 transition-colors"
            >
              Ready to test? Book your water test <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Feb 2026 — Service Areas (SEO). Only rendered when the partner
          has publishable suburbs. Semantic <section> + <h2> + <ul> for
          maximum crawler weight on suburb-level local searches. The
          same suburbs are ALSO emitted in the LocalBusiness JSON-LD
          above (as `areaServed`), so structured + visible signals
          agree. Skipped entirely on the noindex preview alias and
          when service_suburbs is empty (fallback resolve-slug shape).

          Show-more toggle (Feb 2026): partners with 300+ service
          suburbs (e.g. tropical-north) would render a wall of pills
          that visually dominates the page. To stay tidy while
          preserving SEO, ALL suburbs render in the DOM at initial
          paint — the extras just get a Tailwind `hidden` class until
          the user (or Googlebot's expansion heuristics) opens them.
          Googlebot indexes DOM content regardless of `display:none`
          via CSS, so the SEO surface stays intact.
      */}
      {!isPreviewAlias && partner?.service_suburbs?.length > 0 && (
        <ServiceAreasSection
          partner={partner}
          partnerName={partnerName}
          partnerRegion={partnerRegion}
        />
      )}

      {/* Local Partner Section */}
      <section id="local-partner" className="bg-gradient-to-b from-slate-900 to-slate-800 text-white py-14">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold">Local Experts. Professional Testing. Practical Solutions.</h2>
            <p className="text-cyan-100 text-sm mt-3 leading-relaxed">
              Book your water test today and get clear answers about your tank or bore water, supported by your local Pristine Water Systems team.
            </p>
            <Link
              to={bookingHref(slug)}
              data-testid="preview-footer-book-cta"
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-base font-semibold px-6 py-3 mt-6 transition-colors"
            >
              Book Your Water Test <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="mt-10 rounded-2xl bg-white/5 border border-white/10 backdrop-blur p-5 flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-3 flex-1 min-w-[220px]">
              {partnerLogo ? (
                <img src={partnerLogo} alt={partnerName} className="w-12 h-12 rounded-lg object-contain bg-white p-1" />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-cyan-300" />
                </div>
              )}
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-cyan-300">Your Local PWS Partner</div>
                <div className="text-base font-bold text-white">{partnerName}</div>
                {partnerBio && (
                  <p className="text-xs text-cyan-100 mt-1 max-w-md">{partnerBio}</p>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-1.5 text-sm">
              {partnerPhone && (
                <a href={`tel:${partnerPhone}`} data-testid="preview-partner-phone" className="flex items-center gap-2 text-white hover:text-cyan-300">
                  <Phone className="w-4 h-4" /> {partnerPhone}
                </a>
              )}
              {partnerEmail && (
                <a href={`mailto:${partnerEmail}`} data-testid="preview-partner-email" className="flex items-center gap-2 text-white hover:text-cyan-300">
                  <Mail className="w-4 h-4" /> {partnerEmail}
                </a>
              )}
              {!partnerPhone && !partnerEmail && (
                <span className="text-xs text-cyan-200/70 italic">Contact details will appear here once a partner is attributed via ?p= or ?slug=</span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer strip */}
      <footer className="bg-slate-900 border-t border-white/10 text-cyan-100 text-xs py-4">
        <div className="max-w-7xl mx-auto px-4 flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="font-bold text-white">Pristine Water Systems {partnerRegion}</div>
            <div>Your local water specialists</div>
          </div>
          <div>Partnered with AWARE Water Testing — Professional. Reliable. Trusted.</div>
          <div>&copy; {new Date().getFullYear()} Pristine Water Systems</div>
        </div>
      </footer>

      {/* Sticky mobile CTA */}
      <Link
        to={bookingHref(slug)}
        data-testid="preview-sticky-cta"
        className="lg:hidden fixed bottom-3 left-3 right-3 z-30 inline-flex items-center justify-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold py-3 shadow-2xl transition-colors"
      >
        <ArrowRight className="w-4 h-4" /> Book Your Water Test
      </Link>

      {loading && (
        <div className="fixed top-3 right-3 text-xs text-slate-500 bg-white border border-slate-200 rounded px-2 py-1 shadow">
          Loading partner…
        </div>
      )}
    </div>
  );
}
